import type { SaveGoalCardSubmitPayload } from "./types";

const SAVE_GOAL_REJECTED_USER_INPUT_MARKER = "User input: ";

/**
 * 激活成功回传给 agent 的简单提示（与 KMP `buildSaveGoalActivatedToolResult` 逐字一致）。
 * 语义：goal 已创建并激活成功，Plan Trackers 已在跑 —— agent 据此承接下一步。
 */
export const SAVE_GOAL_ACTIVATED_TOOL_MESSAGE =
  "Goal created and activated successfully.";

/** 用户未确认目标、改以自由输入反馈时的 Agent 提示词（英文） */
export const buildSaveGoalRejectedFeedback = (userInput: string): string =>
  [
    "The user did not confirm the goal.",
    "Instead, they shared some thoughts in their own words.",
    "Based on the user's input below, decide the next direction.",
    "",
    `${SAVE_GOAL_REJECTED_USER_INPUT_MARKER}${userInput}`,
  ].join("\n");

/** 从 rejected feedback 中提取用户原文，供卡片只读展示 */
export const extractSaveGoalUserFeedbackDisplay = (
  feedback: string,
): string => {
  const markerIndex = feedback.lastIndexOf(
    SAVE_GOAL_REJECTED_USER_INPUT_MARKER,
  );
  if (markerIndex >= 0) {
    return feedback.slice(
      markerIndex + SAVE_GOAL_REJECTED_USER_INPUT_MARKER.length,
    );
  }

  return feedback;
};

/**
 * 解析 save_goal tool message result（JSON 字符串或对象）。
 */
export const parseSaveGoalResult = (
  value: unknown,
): SaveGoalCardSubmitPayload | undefined => {
  if (value == null || value === "") {
    return undefined;
  }

  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value) as unknown;
    } catch {
      return undefined;
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return undefined;
  }

  const candidate = parsed as SaveGoalCardSubmitPayload;
  if (candidate.type === "activated") {
    return {
      type: "activated",
      goalId: candidate.goalId,
      status: candidate.status,
      ...(candidate.correlationId
        ? { correlationId: candidate.correlationId }
        : {}),
      ...(candidate.message ? { message: candidate.message } : {}),
    };
  }

  if (candidate.type === "confirmed") {
    // 旧 confirm-save 时代的历史消息：归一到 activated 语义（goal 已保存/激活）
    return {
      type: "activated",
      goalId: candidate.goalId ?? "",
      status: candidate.status ?? "ACTIVE",
    };
  }

  if (
    candidate.type === "rejected" &&
    typeof candidate.feedback === "string" &&
    candidate.feedback.trim()
  ) {
    return { type: "rejected", feedback: candidate.feedback.trim() };
  }

  return undefined;
};

export const hasSaveGoalResult = (value: unknown): boolean =>
  value != null && value !== "";

/** 已提交且含 feedback 时返回展示文案 */
export const getSaveGoalResultFeedbackText = (
  value: unknown,
): string | undefined => {
  const parsed = parseSaveGoalResult(value);
  if (parsed?.type === "rejected" && parsed.feedback) {
    return extractSaveGoalUserFeedbackDisplay(parsed.feedback);
  }

  return undefined;
};
